import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  type User,
} from "firebase/auth";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { auth } from "@/lib/firebase";
import { queryClient } from "@/lib/query-client";

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<User>;
  signInWithEmail: (email: string, password: string) => Promise<User>;
  signUpWithEmail: (email: string, password: string) => Promise<User>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // Track the previously signed-in uid so we can drop the React Query cache
  // when the identity changes. `undefined` means "no auth emission yet" — we
  // must not clear on the first event (initial load / session restore), only
  // on a real switch (logout, or signing in as a different account). Without
  // this, a new user reuses the previous admin's cached gate result until
  // `staleTime` expires, briefly showing the app shell instead of "No access".
  const prevUidRef = useRef<string | null | undefined>(undefined);

  // Bumped when the user's profile is refreshed in place (e.g. email just
  // verified). Firebase mutates the same User object, so onAuthStateChanged
  // never re-fires for it — the counter forces consumers to re-render.
  const [authVersion, setAuthVersion] = useState(0);

  useEffect(() => {
    return onAuthStateChanged(auth, (next) => {
      const nextUid = next?.uid ?? null;
      if (prevUidRef.current !== undefined && prevUidRef.current !== nextUid) {
        queryClient.clear();
      }
      prevUidRef.current = nextUid;
      setUser(next);
      setLoading(false);
    });
  }, []);

  // The verification link opens in a NEW tab (/auth/action), so this tab's
  // user stays stale. When an unverified user refocuses the window, reload
  // the profile; if verification just landed, refresh the ID token so
  // callables see the new email_verified claim, and re-render consumers.
  useEffect(() => {
    if (!user || user.emailVerified) return;
    let active = true;
    const onFocus = async () => {
      try {
        await user.reload();
        if (!active || !user.emailVerified) return;
        await user.getIdToken(true);
        setAuthVersion((v) => v + 1);
      } catch {
        // Transient (offline, token revoked) — next focus retries.
      }
    };
    window.addEventListener("focus", onFocus);
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      signInWithGoogle: async () => {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        return result.user;
      },
      signInWithEmail: async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        return result.user;
      },
      signUpWithEmail: async (email, password) => {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        // Email/password accounts start unverified. Verification is not a
        // wall anymore (buyers can use the app right away) — send the email
        // now and the header badge tracks the pending state.
        await sendEmailVerification(result.user);
        return result.user;
      },
      resetPassword: (email) => sendPasswordResetEmail(auth, email),
      signOut: () => fbSignOut(auth),
    }),
    // authVersion forces a new context value when the user object was
    // refreshed in place (emailVerified flip) without an auth event.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, authVersion],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
